// myapp/src/components/common/ProtectedRoute.tsx
import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAppAuth } from '../../context/AppAuthContext';

interface ProtectedRouteProps {
  allowedRoles?: string[];
  allowedPermissions?: string[];
}

export const AdminProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  allowedRoles, 
  allowedPermissions 
}) => {
  const { 
    isAuthenticated, 
    role, 
    permissions, 
    loading 
  } = useAdminAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-950">
        <div className="w-7 h-7 border-2 border-t-transparent rounded-full animate-spin border-rose-500"></div>
      </div>
    );
  }

  // Redirect to correct login page if unauthenticated
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const hasWildcard = permissions.includes('*:*');
  const isBypassRole = role === 'SuperAdmin' || role === 'CompanyAdmin';
  const isRoleAllowed = !allowedRoles || allowedRoles.length === 0 || (!!role && allowedRoles.includes(role));

  // If role is explicitly listed as allowed → grant access immediately (role-based pass)
  if (allowedRoles && allowedRoles.length > 0) {
    if (!role) return <Navigate to="/unauthorized" replace />;
    if (isRoleAllowed) {
      // Role is explicitly allowed — bypass permission checks
      return <Outlet />;
    }
    // Role NOT in allowedRoles — fall through to permission-based access check
    if (!allowedPermissions || allowedPermissions.length === 0) {
      // No permissions to check as fallback — deny
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Permission-based access (fallback when role is not in allowedRoles, or no role restriction set)
  if (allowedPermissions && allowedPermissions.length > 0) {
    if (!hasWildcard && !isBypassRole) {
      const hasPermission = allowedPermissions.some(p => permissions.includes(p));
      if (!hasPermission) {
        return <Navigate to="/unauthorized" replace />;
      }
    }
  }

  return <Outlet />;
};

export const AppProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  allowedRoles, 
  allowedPermissions 
}) => {
  const { 
    isAuthenticated, 
    role, 
    permissions, 
    loading 
  } = useAppAuth();

  // Show spinner while auth state is still loading
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-950">
        <div className="w-7 h-7 border-2 border-t-transparent rounded-full animate-spin border-indigo-500"></div>
      </div>
    );
  }

  // Redirect to correct login page if unauthenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If authenticated but role hasn't resolved yet, keep showing spinner
  // This handles the brief window right after login() sets token but before
  // the role state has propagated through React's render cycle
  if (!role) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-950">
        <div className="w-7 h-7 border-2 border-t-transparent rounded-full animate-spin border-indigo-500"></div>
      </div>
    );
  }

  const hasWildcard = permissions.includes('*:*');
  const isBypassRole = role === 'SuperAdmin' || role === 'CompanyAdmin';

  // If role is explicitly listed as allowed → grant access immediately (role-based pass)
  if (allowedRoles && allowedRoles.length > 0) {
    if (allowedRoles.includes(role)) {
      // Role is explicitly allowed — bypass permission checks entirely
      return <Outlet />;
    }
    // Role NOT in allowedRoles — fall through to permission-based fallback
    if (!allowedPermissions || allowedPermissions.length === 0) {
      // No permissions to check as fallback — deny
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Permission-based access (used when: no role restriction set, OR role not in allowedRoles)
  if (allowedPermissions && allowedPermissions.length > 0) {
    if (!hasWildcard && !isBypassRole) {
      const hasPermission = allowedPermissions.some(p => permissions.includes(p));
      if (!hasPermission) {
        return <Navigate to="/unauthorized" replace />;
      }
    }
  }

  return <Outlet />;
};

